import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { AuthStoreService, GenericListColumn } from '@motivforce/mx-library-angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { Course } from 'src/app/core/model/course/course';
import { CourseGroup } from 'src/app/core/model/course/course-group';
import { CourseStoreService } from 'src/app/core/store/course-store.service';
import { UserStoreService } from 'src/app/core/store/user-store.service';

@UntilDestroy()
@Component({
  selector: 'app-take-a-course-tab-content',
  templateUrl: './take-a-course-tab-content.component.html',
  styleUrls: ['./take-a-course-tab-content.component.scss'],
})
export class TakeACourseTabContentComponent implements OnInit {
  @Input() promotionId: number | null;
  programDateFormat: string;

  courseGroups$ = this.courseStore.currentCourseGroups$;
  currentCourseGroup: CourseGroup | undefined;
  primaryCourses: Course[];
  secondaryCourses: Course[];
  hideMandatoryOptionalCourses = false;
  showLearningPaths = false;
  needToggle = false;
  splitIsgIdgCourse = false;

  searchForm = this.formBuilder.group({
    courseBrandId: [null],
    isPcsd: [null],
    promotionId: [null],
  });

  defaultCourseListColumnsProvider: GenericListColumn[] = [
    {
      columnNameDisplayKey: 'Course name',
      sortable: false,
      fieldName: 'name',
    },
    {
      columnNameDisplayKey: 'Code',
      sortable: false,
      fieldName: 'courseCode',
      width: '150px',
    },
    {
      columnNameDisplayKey: 'Points',
      sortable: false,
      fieldName: 'points',
      width: '90px',
    },
    {
      columnNameDisplayKey: 'Duration',
      sortable: false,
      fieldName: 'courseDuration',
      width: '100px',
    },
    {
      columnNameDisplayKey: 'Take course',
      sortable: false,
      fieldName: 'takeCourse',
      width: '160px',
    },
  ];

  primaryCourseListColumnsProvider: GenericListColumn[] = this.defaultCourseListColumnsProvider;
  secondaryCourseListColumnsProvider: GenericListColumn[] = this.defaultCourseListColumnsProvider;

  constructor(
    private courseStore: CourseStoreService,
    private formBuilder: UntypedFormBuilder,
    private authStore: AuthStoreService,
    private userStore: UserStoreService
  ) {}

  @Input()
  get isPcsd(): boolean | null {
    return this.searchForm.value.isPcsd;
  }

  set isPcsd(value: boolean | null) {
    this.searchForm.patchValue({
      isPcsd: value,
    });

    this.search();
  }

  ngOnInit(): void {
    this.authStore.userSettings$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((userSettings) => {
      this.programDateFormat = (userSettings!.user as any).program?.dateFormat;
    });

    this.userStore.userRegion$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((region) => {
      if (region?.regionCode === 'NA' || region?.regionCode === 'LAS') {
        this.hideMandatoryOptionalCourses = true;
      }
      if (region?.regionCode === 'LAS' || region?.regionCode === 'BR') {
        this.needToggle = true;
        this.searchForm.controls['isPcsd'].setValue(false);
      }
      if (region?.regionCode === 'NA' || region?.regionCode === 'LAS' || region?.regionCode === 'BR') {
        this.showLearningPaths = true;
      }
      if (region?.regionCode === 'ANZ') {
        this.splitIsgIdgCourse = true;
      }
      this.search();
    });

    this.courseStore.currentCoursesResults$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((courses) => {
      let tempCourses: Course[];

      if (this.showLearningPaths) {
        const learningPaths = courses.filter((i) => i.isLearningPath);
        const regularCourses = courses.filter((i) => !i.isLearningPath);
        let items: Course[] = [];

        if (learningPaths.length) {
          items = [{ ...this.EMPTY_COURSE, name: 'Learning paths' }, ...learningPaths];
        }
        if (regularCourses.length) {
          items = [...items, { ...this.EMPTY_COURSE, name: 'Courses' }, ...regularCourses];
        }

        tempCourses = items;
      } else if (this.hideMandatoryOptionalCourses) {
        tempCourses = courses;
      } else {
        const mandatoryItems = courses.filter((i) => i.isMandatory);
        const optionalItems = courses.filter((i) => !i.isMandatory);
        let items: Course[] = [];

        if (mandatoryItems.length) {
          items = [{ ...this.EMPTY_COURSE, name: 'Mandatory' }, ...mandatoryItems];
        }
        if (optionalItems.length) {
          items = [...items, { ...this.EMPTY_COURSE, name: 'Optional' }, ...optionalItems];
        }

        tempCourses = items;
      }

      if (this.splitIsgIdgCourse) {
        this.primaryCourses = tempCourses.filter((course) => course.isPcsd === false);
        this.secondaryCourses = tempCourses.filter((course) => course.isPcsd === true);
      } else {
        this.primaryCourses = tempCourses;
      }
    });

    if (this.promotionId) {
      const pointsColumn = this.defaultCourseListColumnsProvider.find((c) => c.fieldName === 'points');

      if (pointsColumn) {
        pointsColumn.fieldName = 'bonusPoints';
      }
    }

    this.courseStore.getCourseGroups();
  }

  search(): void {
    const { value } = this.searchForm;
    if (this.promotionId) {
      value.promotionId = this.promotionId;
      this.courseStore.searchPromotionCourses(value);
    } else {
      this.courseStore.searchCourses(value);
    }
  }

  clear(): void {
    this.searchForm.patchValue({
      courseBrandId: null,
      isPcsd: null,
    });
    this.currentCourseGroup = undefined;
  }

  get EMPTY_COURSE(): Course {
    return {
      id: 0,
      courseCode: '',
      name: '',
      externalUrl: '',
      isMandatory: null,
      courseDuration: null,
      courseCompletionDate: null,
      isCompleted: null,
      points: null,
      credits: null,
      isLearningPath: null,
      isPcsd: null,
    };
  }
}
